// Theme system. Two palettes (dark, light) live behind a Proxy so existing
// `import { T } from "../theme"` callers keep working - they read whichever
// palette is active at render time. Switching modes flips the module-level
// pointer and notifies subscribers; the React `useTheme()` hook subscribes,
// re-renders the consumer, and the cascade re-renders everything below.
//
// Why this shape: 18 components import T directly and reference T.surface /
// T.text inline. Migrating each one to context-via-hook would touch ~570
// references. Proxy + setters lets the existing call sites swap with zero
// edits while still being React-aware via a tiny subscribe layer.

// Brand accents.
//
// Y is the primary call-to-action color: yellow in dark mode, a bright
// bold turquoise in light mode (since the light-mode page background IS
// yellow now, the call-to-action color flips to its complement so it
// stays visible). Y is exported as `let` so setThemeMode can reassign
// the binding when the user toggles modes; ES module live bindings mean
// every importer reads the new value on its next render. Template
// strings (e.g. `${Y}10`) and direct `background: Y` usages all just
// work - no migration needed.
//
// CREDIT (the credit-balance teal) doesn't swap; credits are a domain-
// specific concept whose color shouldn't change with theme.
const Y_DARK  = "#EEFF44";  // brand yellow (dark mode)
const Y_LIGHT = "#06D6A0";  // bright bold turquoise (light mode)
export let Y = Y_DARK;       // reassigned in setThemeMode below

// CREDIT is the credit-balance accent (header counter, CreditAmount,
// CreditIcon). Same live-binding pattern as Y - dark mode keeps the
// bright cyan-teal that pops on dark; light mode is a bold green-
// leaning teal (NOT cyan/blue) so it reads as "teal" at a glance and
// stays distinct from:
//   - Y_LIGHT (#06D6A0) brand turquoise on buttons (more emerald)
//   - T.title (#1A8B83) page-header teal (more muted, less saturated)
const CREDIT_DARK  = "#00E5CC";
const CREDIT_LIGHT = "#00897C";  // bold green-teal, fully saturated
export let CREDIT = CREDIT_DARK;

// Static font tokens (don't depend on theme).
export const LABEL_FONT = "'Rajdhani', sans-serif";

// Background image. Same source for both modes; light mode applies a CSS
// filter at the consumer (see App.js + LeopardBg in shared.js) to lighten +
// desaturate it rather than ship a second asset.
export const BG_SRC = "/bg.jpg";

// ── Palettes ────────────────────────────────────────────────────────────────
// Same shape; swapping the backing object swaps the visible app theme.
//
// Light palette spec (from user): "light grey with a subtle turquoise hue"
// for the body, lighter for surfaces. Title/danger/success/blue stay close
// to their dark-mode hues since they need to read on both.

const DARK = {
  // Base
  // - `dark` is the "dark ink" token: text color when placed on a bright
  //    accent (Y, T.title), bg color for small inverse badges. In dark
  //    mode this happens to match the page bg color.
  // - `bg` is the page background. In dark mode it's the same as `dark`;
  //    in light mode it diverges (page = pale yellow, dark = near-black).
  dark:    "#0A0F10",  // dark ink (text on bright accents, badge bg)
  bg:      "#0A0F10",  // page background
  surface: "#101819",  // cards, popovers
  surface2:"#161F20",
  surface3:"#1C2628",
  // Borders / dividers
  border:  "#222D2F",
  border2: "#283739",
  // Text
  text:    "#EEF2F3",
  text2:   "#8CAEB3",
  text3:   "#6C9197",
  // Accents
  title:   "#5BBFB8",
  danger:  "#FF2D6F",
  // Softer variant of `danger` for body / monospace error text. Dark mode
  // wants a paler pink so the message reads as "info" not "alarm"; light
  // mode shifts to a clear red (still slightly muted vs the bold icon
  // accent) so error text stops reading as pink on the yellow page.
  dangerText:    "#FF8AAF",
  // Low-alpha bg / border for error info boxes. Tied to the danger hue so
  // dark mode keeps its existing warm red wash; light mode swaps to red-on-
  // yellow which reads as red rather than pink.
  dangerBg:      "rgba(255,45,111,0.08)",
  dangerBorder:  "rgba(255,45,111,0.30)",
  success: "#44FFAA",
  blue:    "#44BBFF",
  // Chrome (nav, header, dropdown menu) - was hardcoded in App.js
  chrome:        "#070B0C",
  chromeBorder:  "#172022",
  // Sticky Generate-row bar. Semi-transparent (chrome hue) so page
  // content shows through slightly as it scrolls under the bar.
  genRowBg:      "rgba(7,12,13,0.6)",
  menuBg:        "#0B1315",
  // Semantic overlays. Match the existing rgba(255,255,255,X) pattern.
  overlay04:     "rgba(255,255,255,0.04)",
  overlay06:     "rgba(255,255,255,0.06)",
  overlay08:     "rgba(255,255,255,0.08)",
  overlay12:     "rgba(255,255,255,0.12)",
  // Media containers (image drop zones, job cards). In dark mode they
  // use the existing white-on-dark overlay pattern; light mode flips
  // to a dark-on-light transparent so cards/dropzones read as defined
  // dark frames against the yellow page rather than disappearing.
  media:         "rgba(255,255,255,0.03)",
  mediaBorder:   "rgba(255,255,255,0.12)",
  // CSS mix-blend-mode applied to the page-bg image overlay. Dark mode
  // composites normally; light mode multiplies the (desaturated) image
  // against the yellow page so the dark circuit lines naturally darken
  // the underlying yellow rather than just tinting it.
  bgBlend:       "normal",
  // Settings-page card background. Mirrors T.surface in dark mode.
  settingsCard:     "#101819",
  // Model picker option tile backgrounds (dropdown content, not the
  // panel itself). Selected vs unselected. Dark mode keeps the tiny
  // alpha overlays that have always been there.
  optionBg:         "rgba(255,255,255,0.02)",
  optionBgSelected: "rgba(238,255,68,0.06)",
  // Border color for unselected option tiles. Separated from T.border
  // because the picker tile borders need to NOT be the brand-yellow-
  // shifted "mustard" T.border in light mode while every other panel /
  // confirm dialog / settings card keeps that token.
  optionBorder:     "rgba(255,255,255,0.10)",
  // Border color for the SELECTED option tile in dark mode = Y (the
  // brand yellow). Same call site uses a thicker stroke than the
  // unselected default for visual emphasis.
  optionBorderSelected: "#EEFF44",
  // Model-icon badge wrapper background (Kling / Gemini / Grok /
  // Recraft logos in the picker, library thumbnails, etc.). Solid
  // black in dark; semi-transparent in light so the icon doesn't read
  // as a heavy black square on the yellow page.
  modelBadgeBg:     "#000",
  // Popover panel bg (model picker dropdown, presets dropdown). Dark
  // keeps T.surface; light mode flips to a yellow that ties the panel
  // into the page bg family rather than appearing as a white card.
  popoverBg:        "#101819",
  // Muted icon color used as inheritance source for placeholder svgs
  // inside drop zones. Dark = faint white, light = visible dark with
  // alpha so icons read as dim-but-present in both modes.
  iconMuted:        "rgba(255,255,255,0.18)",
  // Default border for empty/unfocused text inputs (no value, no
  // focus). Replaces hardcoded rgba(255,255,255,0.15) which was
  // invisible on the yellow page in light mode.
  inputBorder:      "rgba(255,255,255,0.15)",
  // Soft brand color used as text / border on inactive pill states.
  // In dark mode these are the existing `${Y}55` / `${Y}99` alpha-yellow
  // values; light mode flips to a more saturated darker teal so the
  // inactive pills stay visible on the bright yellow page.
  brandSoft:     "rgba(238,255,68,0.60)",   // text on inactive pills (was ${Y}99)
  brandSoftBorder:"rgba(238,255,68,0.33)",  // border on inactive pills (was ${Y}55)
  // Bottom-of-image gradient overlay used to surface action buttons over
  // the user's start/end frame thumbnails. Two strengths:
  //   imageOverlay      - fades from 53% dark, used on the start frame
  //   imageOverlayHeavy - fades from 80% dark, used on the end frame
  imageOverlay:      "linear-gradient(to top,#0A0F1088,transparent 40%)",
  imageOverlayHeavy: "linear-gradient(to top,#0A0F10cc,transparent)",
  // Background image filter applied to bg.jpg. None for dark.
  bgFilter:      "none",
  // Vignette overlay (radial gradient outer color)
  vignette:      "rgba(10,15,16,0.7)",
};

const LIGHT = {
  // Base
  // - `dark` is now the dark-ink color, NOT the page bg. Same role as in
  //    DARK above: text on bright accent buttons (Y / title) and the bg
  //    of small inverse badges. Always near-black so contrast holds on
  //    the bright turquoise Y_LIGHT and the bright yellow page bg.
  // - `bg` is the page background: a pale yellow that's clearly in the
  //    brand-yellow family but soft enough to occupy the whole screen
  //    comfortably. Surfaces step lighter from there so cards / popovers
  //    read as calmer rectangles on top.
  dark:    "#1A1F08",  // dark ink (text on bright accents, badge bg)
  // Page bg is a very light, almost-white grey with a subtle turquoise
  // hue (ties into the Y_LIGHT / title teal accents). The chrome (sidebar
  // / header) still uses the exact brand yellow below so the nav stays
  // the signature color; the page itself is now a calm near-white.
  bg:      "#EFFFFC",  // bright near-white, clearer turquoise tint
  // Surface = pale yellow stack (NOT white) - keeps confirm dialogs,
  // generic popovers, and other T.surface call-sites in the chrome
  // family rather than rendering as bright white panels on the yellow
  // page. Steps lighter so cards-on-cards still have hierarchy.
  surface: "#FAFFA8",
  surface2:"#FCFFC8",
  surface3:"#FEFFEC",
  // Borders / dividers - subtle neutral grey instead of the mustard
  // brand-yellow shifted (#B5C200) that previously defined panel edges.
  // The mustard read as "olive" on the yellow page and clashed with the
  // turquoise accent palette; 15% black is a quiet neutral that defines
  // popover / dropdown / settings card edges without color noise.
  // Chrome (sidebar / header) still uses chromeBorder below since the
  // chrome IS the brand yellow and needs a brand-family edge.
  border:  "rgba(0,0,0,0.15)",
  border2: "rgba(0,0,0,0.20)",
  // Text - near-black + dark slate-teal greys (avoid olive/green-brown
  // tones, which clashed with the yellow page).
  text:    "#1A1F08",
  text2:   "#34494B",  // dark slate-teal for secondary text
  text3:   "#5B7374",  // medium slate-teal for tertiary text
  // Accents
  title:   "#1A8B83",  // page-header teal - distinct from the brand turquoise (Y_LIGHT)
  danger:  "#C82323",  // red, not pink (was #D62356 which read pink on yellow)
  // Body / monospace error text - slightly softer than the bold danger
  // icon accent but still firmly red. Without this the dark-mode #FF8AAF
  // pink leaks into light mode and reads as pink-on-yellow.
  dangerText:    "#A82828",
  dangerBg:      "rgba(200,35,35,0.10)",
  dangerBorder:  "rgba(200,35,35,0.35)",
  success: "#1FA871",
  blue:    "#1F84CF",
  // Chrome (sidebar, top header) = the exact brand yellow Y_DARK. In
  // dark mode Y is the accent on a dark chrome; in light mode the chrome
  // IS the brand yellow and turquoise is the accent.
  chrome:        "#EEFF44",
  // Chrome edge: was the same mustard #B5C200 as T.border but read as
  // olive against the brand-yellow chrome itself; subtle 15% black gives
  // the nav a quiet definition that ties into T.border / T.optionBorder.
  chromeBorder:  "rgba(0,0,0,0.15)",
  // Sticky Generate-row bar: a darker, more turquoise-saturated variation
  // of the page bg, now semi-transparent so page content shows through
  // slightly as it scrolls under the CTA bar (still distinct from the
  // page, not the brand-yellow chrome).
  genRowBg:      "rgba(208,231,225,0.6)",
  // Avatar-chip menu / dropdown bg: a light grey-turquoise, intentionally
  // greyer than the near-white page so the menu reads as a defined
  // floating panel rather than a white card; border + shadow add edge.
  menuBg:        "#D8EBE6",
  // Overlays - flip white-on-dark for dark-on-light
  overlay04:     "rgba(0,0,0,0.04)",
  overlay06:     "rgba(0,0,0,0.06)",
  overlay08:     "rgba(0,0,0,0.08)",
  overlay12:     "rgba(0,0,0,0.10)",
  // Media containers (job cards, image drop zones, expanded-card image/
  // video frames). Transparent dark on yellow gives a "photo frame"
  // feel - content sits in defined panels rather than fighting the
  // bright page. Alpha kept very low so the page tint shows through
  // clearly and the panels stay light overall.
  media:         "rgba(20,28,32,0.06)",
  mediaBorder:   "rgba(0,0,0,0.10)",
  // Soft brand color used as text / border on inactive pill states.
  // Yellow-on-yellow at low alpha disappears in light mode, so we
  // override with darker turquoise variants (matching Y_LIGHT but at
  // higher alphas so the inactive pills stay clearly visible against
  // the yellow page).
  brandSoft:        "rgba(0,128,98,0.95)",  // text on inactive pills
  brandSoftBorder:  "rgba(0,128,98,0.55)",  // border on inactive pills
  // Settings-page card backgrounds. Light mode uses the same light grey-
  // turquoise as the menu / picker panels so settings rows sit close to
  // the page in the new turquoise scheme instead of bright brand yellow.
  // Dark mode keeps the regular surface treatment.
  settingsCard:     "#D8EBE6",
  // Model picker option tiles - turquoise-hued light greys (variations of
  // the page bg), no longer yellow. Unselected sits as a soft tile on the
  // near-white popover panel; selected steps to a deeper, more saturated
  // turquoise and is further emphasized by the bright optionBorderSelected
  // stroke below.
  optionBg:         "#E4F4F0",
  optionBgSelected: "#C7EDE5",
  // Border for unselected tiles. Same neutral grey as T.border so all
  // panel / tile / menu edges share one quiet stroke; the selected tile
  // gets the strong turquoise below for emphasis.
  optionBorder:     "rgba(0,0,0,0.15)",
  // Border color for the SELECTED option tile. Brighter / more vivid
  // than Y_LIGHT (#06D6A0) so it pops against the yellow card it's
  // outlining; paired with a thicker stroke at the call site.
  optionBorderSelected: "#00FFB8",
  // Model-icon badge wrapper at 15% opacity so the icon reads as a
  // soft frame on the yellow page rather than a solid black tile.
  modelBadgeBg:     "rgba(0,0,0,0.15)",
  // Popover / menu panel bg (model picker, presets, image picker drawer,
  // effect preview) - a light grey-turquoise, intentionally greyer than
  // the near-white page so panels read as defined surfaces (not white
  // cards); border + shadow define their edge.
  popoverBg:        "#D8EBE6",
  // Muted icon color inside drop zones. Solid dark with alpha so the
  // placeholder picture-frame icons read clearly without being too
  // bold (matches the muted feel of dark mode's white-alpha pattern).
  iconMuted:        "rgba(26,31,8,0.55)",
  // Default border for empty/unfocused inputs - same low-alpha dark
  // line that T.overlay12 uses, kept as a separate token so input
  // borders can be tuned independently if needed later.
  inputBorder:      "rgba(0,0,0,0.18)",
  // Bottom-of-image gradient overlays. Much lighter alpha in light mode
  // so the gradient hints at a button-readability shade without putting
  // a heavy dark band on the yellow page.
  imageOverlay:      "linear-gradient(to top,rgba(0,0,0,0.18),transparent 40%)",
  imageOverlayHeavy: "linear-gradient(to top,rgba(0,0,0,0.30),transparent)",
  // Invert the dark bg.jpg so the texture reads as a light overlay, then
  // give it a faint turquoise wash instead of a flat grey: grayscale(1)
  // neutralizes the source colors, sepia(0.4)+hue-rotate(130deg) recolor
  // the neutral toward turquoise (~175deg), saturate(1.3) keeps the tint
  // gentle. Multiply-blended into the near-white page (opacity 0.25 in
  // App.js) so the structural lines darken the page underneath.
  bgFilter:      "invert(1) grayscale(1) sepia(0.4) hue-rotate(130deg) saturate(1.3) brightness(0.95) contrast(1)",
  bgBlend:       "multiply",
  // Vignette in the (now turquoise) brand hue family so the page edges
  // fade toward turquoise rather than the old olive/chartreuse wash that
  // was tinting the whole near-white page yellow-green.
  vignette:      "rgba(0,158,140,0.22)",
};

const PALETTES = { dark: DARK, light: LIGHT };

// ── Active mode + listener bus ──────────────────────────────────────────────

const STORAGE_KEY = "katana.theme";

function _initialMode() {
  if (typeof window === "undefined") return "dark";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch (_) {}
  return "dark";
}

let _mode = _initialMode();
// Ephemeral display override (NOT persisted). Forces the login / pre-auth
// screens to dark regardless of the saved preference; cleared once the
// user is authenticated so their real choice applies. Starts at "dark" so
// the very first paint - before Firebase resolves auth - is never light.
let _override = "dark";
const _listeners = new Set();

// The mode actually shown = override when set, else the saved mode.
function _activeMode() { return _override || _mode; }

// Push an effective mode into the live brand bindings, the <html> attr,
// the body, and subscribers. Shared by setThemeMode (persisted) and
// setThemeOverride (ephemeral) so both stay in sync.
function _apply(m) {
  // ES module exports of `let` bindings are live - every `import { Y }` /
  // `import { CREDIT }` reads through to these, so no per-file migration.
  Y      = m === "light" ? Y_LIGHT      : Y_DARK;
  CREDIT = m === "light" ? CREDIT_LIGHT : CREDIT_DARK;
  try {
    if (typeof window !== "undefined") {
      // Sync the html data attribute so CSS in index.css can react too
      // (scrollbar colors, body background pre-React).
      document.documentElement.setAttribute("data-theme", m);
      document.body.style.background = PALETTES[m].bg;
      document.body.style.color = PALETTES[m].text;
    }
  } catch (_) {}
  _listeners.forEach(fn => { try { fn(m); } catch (_) {} });
}

// Initialize the live brand bindings from the active (override-aware)
// mode so the very first render reads the right values.
Y      = _activeMode() === "light" ? Y_LIGHT      : Y_DARK;
CREDIT = _activeMode() === "light" ? CREDIT_LIGHT : CREDIT_DARK;

// Returns the mode currently shown (override-aware). The avatar / settings
// toggles only render post-auth where no override is active, so this stays
// correct for them too.
export function getThemeMode() { return _activeMode(); }

export function setThemeMode(mode) {
  if (mode !== "dark" && mode !== "light") return;
  _mode = mode;
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (_) {}
  _apply(_activeMode());
}

// Force ('dark' | 'light') or clear (null) the ephemeral display override
// without touching the saved preference. App sets 'dark' while logged out
// and clears it once authenticated.
export function setThemeOverride(mode) {
  _override = (mode === "dark" || mode === "light") ? mode : null;
  _apply(_activeMode());
}

export function subscribeTheme(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// Run once at module load so the html attr + body bg match the active
// (override-aware) mode before React mounts.
if (typeof window !== "undefined") {
  try {
    const m = _activeMode();
    document.documentElement.setAttribute("data-theme", m);
    document.body.style.background = PALETTES[m].bg;
    document.body.style.color = PALETTES[m].text;
  } catch (_) {}
}

// ── Proxy palette ───────────────────────────────────────────────────────────
// Components read T.surface / T.text / etc. as before. Reads route through
// the active palette; switching mode + re-rendering the tree gives every
// inline style the new value.

export const T = new Proxy({}, {
  get(_target, prop) {
    const p = PALETTES[_activeMode()];
    if (prop in p) return p[prop];
    // Allow callers to peek at the mode itself via T._mode without an extra import
    if (prop === "_mode") return _activeMode();
    return undefined;
  },
  has(_target, prop) { return prop in PALETTES[_activeMode()]; },
  ownKeys() { return Object.keys(PALETTES[_activeMode()]); },
  getOwnPropertyDescriptor(_target, prop) {
    if (prop in PALETTES[_activeMode()]) {
      return { enumerable: true, configurable: true, value: PALETTES[_activeMode()][prop] };
    }
    return undefined;
  },
});

// `inp` was a static const built from T.title. Convert to a Proxy so the
// borderBottom + color follow the active mode at render time. Both direct
// reads (style={inp}) and spread (style={{...inp, foo}}) work: spread
// iterates own enumerable keys via ownKeys + getOwnPropertyDescriptor.
function _buildInp() {
  return {
    width: "100%",
    background: "transparent",
    border: "none",
    borderBottom: `1px solid ${_activeMode() === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"}`,
    borderRadius: 0,
    color: PALETTES[_activeMode()].title,
    fontSize: 14,
    padding: "10px 2px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "Mulish,sans-serif",
    transition: "border-color 0.2s",
  };
}

export const inp = new Proxy({}, {
  get(_t, prop) { return _buildInp()[prop]; },
  ownKeys() { return Object.keys(_buildInp()); },
  getOwnPropertyDescriptor(_t, prop) {
    const v = _buildInp()[prop];
    if (v === undefined) return undefined;
    return { enumerable: true, configurable: true, writable: true, value: v };
  },
});

export const LOGO_SRC = "/logo.png";
